import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ActiveChatTopBar.module.css';
import ActiveChatSettingsMenu from './ActiveChatSettingsMenu';
import { shareIcon, clipboardIcon, visibilityPrivateIcon } from '../../assets/icons';
import { api } from '../../api';
import { ChatDetailResponse, ChatMember } from '../../api/types/chat';

interface ActiveChatTopBarProps {
  chat: ChatDetailResponse | null;
  onBack: () => void;
  activePlayable: {
    id: string;
    title: string;
    behaviorType: 'foreground' | 'background';
    backgroundIcon?: string;
  } | null;
  onStopBackground: () => void;
  showChatSettings?: boolean;
  onChatSettingsClick: () => void;
}

function ActiveChatTopBar({ 
  chat, 
  onBack, 
  activePlayable, 
  onStopBackground,
  showChatSettings,
  onChatSettingsClick 
}: ActiveChatTopBarProps) {
  const navigate = useNavigate();
  const [showBackgroundMenu, setShowBackgroundMenu] = useState<boolean>(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState<boolean>(false);
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [notificationType, setNotificationType] = useState<'copied' | 'private'>('copied');
  const characterMember = chat?.members?.find(m => m.member_type === 'character');
  const [characterVisibility, setCharacterVisibility] = useState<string | null>(null);

  const hasOnlyGreetingMessage = chat?.messages?.length === 1;

  // Fetch character visibility when character member changes
  useEffect(() => {
    const fetchCharacterVisibility = async () => {
      if (characterMember) {
        try {
          const character = await api.characters.get(characterMember.username);
          setCharacterVisibility(character.visibility);
        } catch (err) {
          console.error('Failed to fetch character details:', err);
        }
      }
    };

    fetchCharacterVisibility();
  }, [characterMember]);

  const handleSettingsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent click from bubbling
    setShowSettingsMenu(!showSettingsMenu);
  };

  const handleSetPersona = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent click from bubbling
    onChatSettingsClick();
    setShowSettingsMenu(false);
  };

  const handleShareCharacter = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent click from bubbling
    
    if (characterMember) {
      // Check if character is private
      if (characterVisibility === 'private') {
        // Show private notification
        setNotificationType('private');
        setShowNotification(true);
        setTimeout(() => {
          setShowNotification(false);
        }, 3000);
      } else {
        // Create the character profile URL
        const shareUrl = `${window.location.origin}/chars/${characterMember.username}`;
        
        // Copy to clipboard
        navigator.clipboard.writeText(shareUrl)
          .then(() => {
            setNotificationType('copied');
            setShowNotification(true);
            setTimeout(() => {
              setShowNotification(false);
            }, 3000);
          })
          .catch(err => {
            console.error('Failed to copy: ', err);
          });
      }
    }
    
    setShowSettingsMenu(false);
  };

  // Add click handler to close menus when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Element;
      const isButtonClick = target.closest(`.${styles.backgroundButton}`);
      const isMenuClick = target.closest(`.${styles.backgroundMenu}`);
      
      // Don't close if clicking on the button or menu itself
      if (isButtonClick || isMenuClick) {
        return;
      }
      
      if (showSettingsMenu) {
        setShowSettingsMenu(false);
      }
      if (showBackgroundMenu) {
        setShowBackgroundMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSettingsMenu, showBackgroundMenu]);

  return (
    <>
      <div className={styles.header}>
        <button onClick={onBack} className={styles.backButton}>
          ‹
        </button>
        <div className={styles.chatInfo}>
          <img 
            src={chat?.icon}
            alt={chat?.display_name}
            className={styles.avatar}
            onClick={() => characterMember && navigate(`/chars/${characterMember.username}`)}
          />
        </div>
        {/* Always show the settings button, regardless of message count */}
        <div className={styles.backgroundControls}>
          <button 
            onClick={handleSettingsClick} 
            className={`${styles.backgroundButton} ${showSettingsMenu ? styles.active : ''}`}
            data-testid="chat-settings-button"
          >
            •••
          </button>
          {showSettingsMenu && (
            <ActiveChatSettingsMenu 
              onSetPersona={() => {
                onChatSettingsClick();
                setShowSettingsMenu(false);
              }} 
              onShareCharacter={() => {
                handleShareCharacter({ stopPropagation: () => {} } as React.MouseEvent);
              }}
              showSetPersona={hasOnlyGreetingMessage}
            />
          )}
        </div>
        {activePlayable?.behaviorType === 'background' && activePlayable?.backgroundIcon && (
          <div className={styles.backgroundControls}>
            <button 
              className={`${styles.backgroundButton} ${showBackgroundMenu ? styles.active : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowBackgroundMenu(!showBackgroundMenu);
              }}
            >
              <img src={activePlayable.backgroundIcon} alt={activePlayable.title} />
            </button>
            {showBackgroundMenu && (
              <div className={styles.backgroundMenu}>
                <button 
                  className={styles.menuItem}
                  onClick={() => {
                    onStopBackground();
                    setShowBackgroundMenu(false);
                  }}
                >
                  Close {activePlayable.title}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      
      {showNotification && (
        <div className={styles.notification}>
          {notificationType === 'copied' ? (
            <>
              <img src={clipboardIcon} alt="Copied" className={styles.notificationIcon} />
              <span>Copied to clipboard</span>
            </>
          ) : (
            <>
              <img 
                src={visibilityPrivateIcon} 
                alt="Private" 
                className={styles.notificationIcon} 
                style={{ filter: 'brightness(0) invert(1)' }} 
              />
              <span>This character is private</span>
            </>
          )}
        </div>
      )}
    </>
  );
}

export default ActiveChatTopBar; 